import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { Drawer } from '../../../../shared/drawer/drawer'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { LeaveGuard } from '../../../../shared/sso-workspace/leave-guard'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { useAlertRoutingAccess } from './access'
import { useCatalog } from './catalog'
import { SchemaForm, validateSchema } from './schema-form'
import { splitConfig } from './editor-steps'
import { VerifySnippet } from './verify-snippet'
import { SecretPanel } from './secret-panel'
import { addAddress, createTarget, deleteTarget, getTarget, removeAddress, setPersonal, setSecret, testTarget, updateTarget } from './alert-routing-api'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../shared/audit/history/audit-record-context'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TARGETS_TAB = '/admin/alert-routing?tab=targets'

// A target: its generic config form (the action type's ConfigSchema), its
// write-only secrets (replace, or rotate a generated one, shown once), an
// email target's addresses, the personal data switch (asked first), Send
// test and Delete (spec §12, §20.3).
// The drawer's state belongs to one target: moving to another target (back,
// forward, a link) remounts it, so a shown secret, a test run, a status or
// an error never carries across. The one exception is create: the address
// moves from /new to the new id and the panel with its secret must stay.
export const TargetDrawer = () => {
  const { targetID } = useParams()
  const created = useRef(null)
  const key = useRef(targetID)
  if (created.current === null || String(created.current) !== targetID) {
    created.current = null
    key.current = targetID
  }
  return <TargetDrawerBody key={key.current} onCreated={id => { created.current = id }} />
}

const TargetDrawerBody = ({ onCreated }) => {
  const { targetID } = useParams()
  const isNew = targetID === 'new'
  const type = new URLSearchParams(useLocation().search).get('type') || 'email'
  const history = useHistory()
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const [t, setT] = useState(isNew ? { type, name: '', config: {}, secrets: {}, addresses: [], allowPersonal: false } : null)
  const [form, setForm] = useState({})
  // History: whose target this is, once loaded (null: a platform target),
  // for a reader without SYS_AUDIT_LOG (record-routes.js recordOrg)
  const loaded = !isNew && Boolean(t)
  useAuditRecord({ type: 'alert_target', id: loaded ? targetID : undefined, organizationID: loaded ? (t.organizationID ?? null) : undefined })
  const [errors, setErrors] = useState({})
  const [shown, setShown] = useState(null)
  const [address, setAddress] = useState('')
  const [addressError, setAddressError] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [testRun, setTestRun] = useState(null)
  const [status, setStatus] = useState('')
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  // The target just created: the address moves to its id, and there is no
  // need to fetch what the server has just returned.
  const created = useRef(null)
  const close = () => history.push(TARGETS_TAB)

  useEffect(() => {
    if (isNew || String(created.current) === targetID) return
    getTarget(targetID).then(v => { setT(v); setForm(v.config || {}) }, err => setError(serverMessage(err, "This target couldn't be loaded."), err))
  }, [targetID, isNew, setError])

  if (isNew && !canEdit) return <Drawer open title="New target" onClose={close}><Notice tone="warn">{reason}</Notice></Drawer>
  if (!t) return <Drawer open title="Target" onClose={close}>{error ? <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></> : <p className="sw-hint">Loading…</p>}</Drawer>

  const at = catalog.actionTypes.find(a => a.type === t.type) || { configSchema: [], capabilities: {} }
  const capabilities = at.capabilities || {}
  const secretFields = at.configSchema.filter(f => f.secret)
  const fieldLabel = key => (at.configSchema.find(f => f.key === key) || { label: key }).label
  const run = async fn => {
    if (busy) return
    setBusy(true)
    try { setError(null); setStatus(''); await fn() } catch (err) { setError(serverMessage(err, "That didn't work."), err) } finally { setBusy(false) }
  }

  const save = () => run(async () => {
    const e = validateSchema(at.configSchema, form, isNew)
    if (!t.name.trim()) e.name = 'Give the target a name.'
    setErrors(e)
    if (Object.keys(e).length) return
    if (isNew) {
      const out = await createTarget({ type: t.type, name: t.name.trim(), ...splitConfig(at.configSchema, form) })
      created.current = out.target.id
      onCreated(out.target.id)
      setT(out.target)
      setForm(out.target.config || {})
      if (out.generatedSecrets && Object.keys(out.generatedSecrets).length) setShown({ secrets: out.generatedSecrets })
      history.replace(`/admin/alert-routing/targets/${out.target.id}`)
    } else {
      const out = await updateTarget(t.id, { name: t.name.trim(), config: form })
      setT(out)
      setStatus('Saved.')
    }
  })

  const add = () => {
    const value = address.trim()
    if (!EMAIL.test(value)) { setAddressError('Enter an email address, like name@example.org.'); return }
    setAddressError(null)
    run(async () => { setT(await addAddress(t.id, { address: value, label: '' })); setAddress('') })
  }

  const usedBy = t.usedBy || 0

  return (
    <Drawer open size="lg" title={isNew ? `New ${at.label || t.type} target` : t.name} onClose={close}>
      <LeaveGuard when={Boolean(shown)} label="secret not copied" title="Copy the secret first"
        message="The secret won't be shown again. Leave without copying it?" leaveLabel="Leave and lose the secret" />
      <div className="ar-drawer">
        {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}

        <fieldset disabled={!canEdit} className="ar-form ar-readonly">
          <div className="sw-field">
            <label className="sw-label ar-required" htmlFor="ar-target-name">Target name</label>
            <input id="ar-target-name" className={`form-control${errors.name ? ' is-invalid' : ''}`} value={t.name} onChange={e => setT({ ...t, name: e.target.value })} />
            {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
          </div>
          <SchemaForm schema={at.configSchema} value={form} secretsSet={Object.fromEntries(Object.entries(t.secrets || {}).map(([k, v]) => [k, Boolean(v && v.set)]))}
            onChange={setForm} isNew={isNew} errors={errors} />
          {isNew && capabilities.perAddress && <p className="sw-hint m-0">Add the email addresses once the target is created.</p>}
        </fieldset>
        {canEdit && (
          <div className="ar-row mt-2">
            <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={save}>{isNew ? 'Create target' : 'Save'}</button>
            {status && <span className="sw-status" role="status">{status}</span>}
          </div>
        )}

        {shown && <SecretPanel secrets={shown.secrets} labelOf={fieldLabel} rotated={shown.rotated} onConfirm={() => setShown(null)} />}

        {!isNew && canEdit && secretFields.length > 0 && (
          <section className="ar-section" aria-label="Secrets">
            <h3 className="h6">Secrets</h3>
            <p className="sw-hint">Secrets can be replaced but never read back.</p>
            {secretFields.map(f => (
              <div key={f.key} className="ar-row mt-2">
                {f.generate ? (
                  <button type="button" className="btn sw-btn btn-sm" disabled={busy || Boolean(shown)} onClick={() => run(async () => {
                    const r = await setSecret(t.id, { field: f.key, rotate: true })
                    setShown({ secrets: { [f.key]: r.secret }, rotated: true })
                  })}>Rotate {f.label.toLowerCase()}</button>
                ) : (
                  <SecretReplace field={f} busy={busy} onSave={secret => run(async () => { await setSecret(t.id, { field: f.key, secret }); setT(await getTarget(t.id)) })} />
                )}
              </div>
            ))}
          </section>
        )}
        {t.type === 'webhook' && <VerifySnippet />}

        {capabilities.perAddress && !isNew && (
          <section className="ar-section" aria-label="Addresses">
            <h3 className="h6">Addresses</h3>
            {t.addresses.length === 0
              ? <p className="sw-hint">No addresses yet. Alerts sent here go to nobody until one is added.</p>
              : <ul className="list-unstyled ar-addresses">{t.addresses.map(a => (
                <li key={a.id} className="ar-row">
                  <span className="ar-address">{a.address}{a.label && <span className="sw-cell-sub">{a.label}</span>}</span>
                  {canEdit && <button type="button" className="btn btn-sm sw-btn-outline-danger" disabled={busy} aria-label={`Remove ${a.address}`}
                    onClick={() => run(async () => setT(await removeAddress(t.id, a.id)))}>Remove</button>}
                </li>))}</ul>}
            {canEdit && (
              <div className="ar-row">
                <label className="visually-hidden" htmlFor="ar-addr">Add an address</label>
                <input id="ar-addr" className={`form-control${addressError ? ' is-invalid' : ''}`} type="email" placeholder="name@example.org" value={address}
                  onChange={e => setAddress(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
                <button type="button" className="btn sw-btn btn-sm" disabled={busy} onClick={add}>Add</button>
                {addressError && <div className="invalid-feedback d-block">{addressError}</div>}
              </div>
            )}
          </section>
        )}

        {!isNew && t.type !== 'email' && (
          <section className="ar-section" aria-label="Personal data">
            <div className="form-check">
              <input id="ar-personal" className="form-check-input" type="checkbox" checked={Boolean(t.allowPersonal)} disabled={!canEdit || busy}
                onChange={() => (t.allowPersonal ? run(async () => setT(await setPersonal(t.id, false))) : setConfirm('personal'))} />
              <label className="form-check-label" htmlFor="ar-personal">Allow personal data</label>
            </div>
            <p className="sw-hint m-0">Off: names, email addresses and messages are left out of what this target receives.</p>
          </section>
        )}

        {!isNew && canEdit && (
          <section className="ar-section" aria-label="Test and delete">
            <div className="ar-row">
              <button type="button" className="btn sw-btn" disabled={busy}
                onClick={() => run(async () => setTestRun((await testTarget(t.id)).run))}>Send test</button>
              <button type="button" className="btn sw-btn-outline-danger" disabled={busy || usedBy > 0 || Boolean(shown)} onClick={() => setConfirm('delete')}>Delete</button>
            </div>
            {usedBy > 0 && <p className="sw-hint m-0">{plural(usedBy, 'route')} {usedBy === 1 ? 'uses' : 'use'} this target, so it can't be deleted. Take it off {usedBy === 1 ? 'that route' : 'those routes'} first.</p>}
          </section>
        )}
        {testRun && <TestResult run={testRun} />}
      </div>

      <ConfirmDialog open={confirm === 'personal'} title="Allow personal data?" confirmText="Allow personal data" busy={busy}
        body="Alerts sent here will include names, email addresses and messages people wrote, including staff names, and they leave EducationXR. This is recorded in the audit log."
        onCancel={() => setConfirm(null)} onConfirm={() => run(async () => { setT(await setPersonal(t.id, true)); setConfirm(null) })} />
      <ConfirmDialog open={confirm === 'delete'} title={`Delete the target "${t.name}"?`} confirmText="Delete target" busy={busy}
        body="No route uses it. It stops at once and can't be brought back."
        onCancel={() => setConfirm(null)} onConfirm={() => run(async () => { await deleteTarget(t.id); setConfirm(null); close() })} />
    </Drawer>
  )
}

const TestResult = ({ run }) => {
  const deliveries = run.deliveries || []
  return (
    <div className="ar-card mt-2" role="status">
      {deliveries.length === 0
        ? <p className="m-0">Nothing was sent: the target is off, or it has nowhere to send yet.</p>
        : <ul className="ar-run m-0">{deliveries.map(d => (
          <li key={d.id}>{d.destination}: {(d.history || []).length
            ? d.history.map((h, i) => <span key={i}>{h.outcome} · {h.providerStatus || 'no answer'} </span>)
            : d.status}</li>))}</ul>}
      {run.alert && run.alert.id && <Link to={`/admin/alert-routing/runs/${run.alert.id}`}>Open in run history</Link>}
    </div>
  )
}

// A secret that is set can be replaced, never read: SchemaForm already says
// "<label>: set", so this draws only the replace input and button.
const SecretReplace = ({ field, busy, onSave }) => {
  const [v, setV] = useState('')
  const id = `ar-replace-${field.key}`
  return (
    <>
      <label className="visually-hidden" htmlFor={id}>New {field.label}</label>
      <input id={id} className="form-control" type="password" autoComplete="new-password" placeholder={`Replace ${field.label.toLowerCase()}`}
        value={v} onChange={e => setV(e.target.value)} />
      <button type="button" className="btn sw-btn btn-sm" disabled={!v || busy} onClick={() => { onSave(v); setV('') }}>Replace</button>
    </>
  )
}
